<template>
    <ElevatedCard :widthType="WidthType.third" class="container-widget">
        <HeaderWidget
            :iconUrl="loadIcon"
            :subtitle="$t('outPreventiveWidget.subtitle')"
            :title="widgetTitle"
            @click="navigateTo(EtatsTravaux.aRealiser)"
        />
        <ReglePastille v-if="widget" cle="dashboard.widgetPerimetreCommun" />
        <div v-if="widget" class="site-widget-container">
            <span v-if="widget.sites == null || widget.sites.length > 1" class="site-widget">{{
                $t('multisites')
            }}</span>
            <span v-for="site in widget.sites" v-else :key="site.id" class="site-widget">
                {{ site.description }}
                <span v-if="site.id !== widget.sites[widget.sites.length - 1].id">, </span>
            </span>
        </div>
        <NoData
            v-if="
                !isLoading &&
                !getPreventiveOutData.nbEnCours &&
                !getPreventiveOutData.nbARealiser &&
                !getPreventiveOutData.nbRealisees
            "
            class="no-data"
            textNoData="widget.preventive.noData"
        />
        <div v-else class="container-tiles">
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('outPreventiveWidget.nbARealiser')"
                :titleNotBold="$t('outPreventiveWidget.intervention', getPreventiveOutData.nbARealiser)"
                :value="getPreventiveOutData.nbARealiser"
                class="tile"
                color="--a-realiser"
                @click="navigateTo(EtatsTravaux.aRealiser)"
            />
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('outPreventiveWidget.nbEnCours')"
                :titleNotBold="$t('outPreventiveWidget.intervention', getPreventiveOutData.nbEnCours)"
                :value="getPreventiveOutData.nbEnCours"
                class="tile"
                color="--en-cours"
                @click="navigateTo(EtatsTravaux.enCours)"
            />
            <Tile
                :isLoading="isLoading"
                :titleInBold="$t('outPreventiveWidget.nbRealisees', getPreventiveOutData.nbRealisees)"
                :titleNotBold="$t('outPreventiveWidget.intervention', getPreventiveOutData.nbRealisees)"
                :value="getPreventiveOutData.nbRealisees"
                class="tile"
                color="--fait"
                @click="navigateTo(EtatsTravaux.realise)"
            />
        </div>
    </ElevatedCard>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ElevatedCard, { WidthType } from '@/components/ui-components/ElevatedCard.vue';
import HeaderWidget from '@/components/ui-components/HeaderWidget.vue';
import NoData from '@/components/ui-components/NoDataWidget.vue';
import Tile from '@/components/ui-components/Tile.vue';
import { store } from '@/plugins/store';
import { PreventiveOutAction } from '../store/modules/PreventiveOutWidget';
import { SuiviInterventionActions } from '@/modules/SuiviInterventions/store/action-types';
import { EtatsTravaux } from '@/models/InterventionNonPreventive';
import { Widget } from '@/models/Widget';
import { FiltreStats } from '@/models/FiltreStats';
import { Site } from '@/models/NavFilter';
import { SUIVI_INTERVENTION } from '@/modules/SuiviInterventions/store/mutation-types';
import PreventiveOutWidgetItems from '@/models/PreventiveOutWidgetItems';
import { EquipementAction } from '@/modules/Equipements/store/actions-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

export default defineComponent({
    name: 'PreventiveOut',
    components: { ElevatedCard, Tile, HeaderWidget, NoData, ReglePastille },
    props: {
        widget: { type: Object as PropType<Widget>, required: false },
        equipementInterventionNonPreventiveData: {
            type: Object as PropType<PreventiveOutWidgetItems>,
            required: false,
        },
        equipementInterventionNonPreventiveLoading: { type: Boolean, default: false },
        isBim: { type: Boolean, default: false },
        title: { type: String, required: false },
    },
    async mounted() {
        if (this.equipementInterventionNonPreventiveData) {
            return;
        }
        const siteIds = this.widget?.sites == null ? [] : this.widget?.sites.map(site => site.id);
        let filtre: FiltreStats = { siteIds: siteIds };
        if (filtre.siteIds.length === this.sitesTotal.length) {
            filtre = { siteIds: [] };
        }
        await store.dispatch(`widgetTravaux/${PreventiveOutAction.GET_PREVENTIVE_OUT_DATA}`, filtre);
    },
    setup() {
        return {
            WidthType,
            EtatsTravaux,
        };
    },
    computed: {
        isLoading() {
            return this.widget
                ? this.$store.state.homeState.widgetTravaux.isLoading
                : this.equipementInterventionNonPreventiveLoading;
        },
        getPreventiveOutData() {
            return this.equipementInterventionNonPreventiveData || this.$store.getters.getPreventiveOut;
        },
        loadIcon() {
            return require('@/assets/img/menu/handyman.png');
        },
        sitesTotal(): Site[] {
            return store.getters['siteList/getListValue'];
        },
        widgetTitle() {
            return this.title || (this.widget ? this.widget.libelle : '');
        },
    },
    methods: {
        navigateTo(etat: EtatsTravaux) {
            if (this.isBim) {
                this.$store.dispatch(`equipementState/${EquipementAction.HISTORIQUE_TRAVAUX_NAV_FILTER}`, [etat]);
                return;
            }
            if (!store.getters.getUserInformations.fonctions.includes('consulter.interventionNP')) {
                return;
            }
            this.$store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.RESET_ITEMS}`);
            const sites: Site[] = this.widget?.sites == null ? [] : this.widget?.sites.map(site => ({ id: site.id }));
            this.$store.commit(`suiviInterventionModuleState/${SUIVI_INTERVENTION.SET_FILTER_SITE}`, sites);
            this.$store.dispatch(`suiviInterventionModuleState/${SuiviInterventionActions.NAV_FILTER}`, [etat]);
            this.$router.push('/suivi-travaux');
        },
    },
});
</script>

<style scoped>
.container-tiles {
    padding: 10px 10px 8%;
    display: flex;
    justify-content: center;
    flex-direction: row;
    flex-grow: 1;
    gap: 25px;
    align-items: center;
    height: 180px;
    width: 86%;
    margin-left: auto;
    margin-right: auto;
}
@media screen and (max-width: 1600px) {
    .container-tiles {
        gap: 10px;
        width: 95%;
    }
}
.container-widget {
    display: flex;
    flex-direction: column;
    background-color: var(--white);
}
.tile {
    margin: 5px 0;
    cursor: pointer;
    height: 125px;
    max-height: 20vh !important;
}
</style>
